
import { useEffect, useMemo, useRef, useState } from "react";
import { useSpeakingParticipants } from "@livekit/components-react";
import styled from "styled-components";
import ParticipantComponent from "./ParticipantComponent";
import ScreenShareParticipantComponent from "./ScreenShareParticipantComponent";
import ScreenShareParticipant from "./ScreenShareParticipant";
import { ReactComponent as BackArrowIcon } from "../../Assets/iconsv2/generic/backArrow.svg";

const ScreenSharingGrid = ({
    callInfo,
    participants,
    screenSharingParticipant,
    screenSharingParticipants,
    handleSelectScreenShareParticipant,
    manuallySelectedScreenShare,
    cameraStripCollapsed = false,
    onToggleCameraStrip,
    controlBar,
}) => {

    const isParticipantElementScreenShareDisabled = screenSharingParticipants.length > 0

    const activeSpeakers = useSpeakingParticipants();
    const [speakerOrder, setSpeakerOrder] = useState([]);
    const previouslyActiveRef = useRef(new Set());

    useEffect(() => {
        const currentlyActiveIds = new Set(activeSpeakers.map((speaker) => speaker.identity));
        const newlyActive = activeSpeakers.filter(
            (speaker) => !previouslyActiveRef.current.has(speaker.identity)
        );

        if (newlyActive.length > 0) {
            setSpeakerOrder((prevOrder) => {
                let nextOrder = prevOrder;
                newlyActive.forEach((speaker) => {
                    nextOrder = [speaker.identity, ...nextOrder.filter((id) => id !== speaker.identity)];
                });
                return nextOrder;
            });
        }

        previouslyActiveRef.current = currentlyActiveIds;
    }, [activeSpeakers]);

    const orderedParticipants = useMemo(() => {
        const participantsById = new Map(participants.map((p) => [p.identity, p]));
        const recentSpeakers = speakerOrder
            .filter((id) => participantsById.has(id))
            .map((id) => participantsById.get(id));
        const rest = participants.filter((p) => !speakerOrder.includes(p.identity));
        return [...recentSpeakers, ...rest];
    }, [participants, speakerOrder]);

    return (
        <Wrapper>
            <FocusViewContainer>

                {!cameraStripCollapsed && (
                    <ParticipantsListWrapper>
                        {screenSharingParticipants.map((screenShareParticipant, index) => (
                            <ScreenShareParticipant
                                screenShareParticipant={screenShareParticipant}
                                callInfo={callInfo}
                                key={index}
                                handleSelectScreenShareParticipant={handleSelectScreenShareParticipant}
                                isSelected={
                                    manuallySelectedScreenShare &&
                                    screenShareParticipant.participant.identity ===
                                    screenSharingParticipant?.participant.identity
                                }
                            />
                        ))}
                        {orderedParticipants.map((participant) => (
                            <ParticipantComponent
                                participant={participant}
                                callInfo={callInfo}
                                key={participant.identity}
                                grid={true}
                                disableScreenShareDisplay={isParticipantElementScreenShareDisabled}
                            />
                        ))}
                    </ParticipantsListWrapper>
                )}

                <FocusWrapper>
                    <CameraToggleBtn onClick={onToggleCameraStrip} $collapsed={cameraStripCollapsed}>
                        <BackArrowIcon />
                    </CameraToggleBtn>
                    <ScreenShareParticipantComponent
                        participant={screenSharingParticipant.participant}
                        callInfo={callInfo}
                        key={screenSharingParticipant.identity}
                        grid={false}
                        focused={true}
                    />
                    {controlBar}
                </FocusWrapper>
            </FocusViewContainer>
        </Wrapper>
    );
};

export default ScreenSharingGrid;
const Wrapper = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const FocusViewContainer = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  overflow: hidden;
  gap: 20px;
  padding: 5px;
`;

const ParticipantsListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: auto;
  width: 100%;
  min-width: 200px;
  max-width: 250px;
  gap: 10px;
`;

const FocusWrapper = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  overflow: hidden;
  position: relative;
`;

const CameraToggleBtn = styled.button`
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: none;
  background: ${({ theme }) => theme.primaryButton.color};
  color: #fff;
  cursor: pointer;
  padding: 0;
  opacity: 0.85;
  transition: opacity 0.15s;

  svg {
    width: 12px;
    height: 12px;
    transform: ${({ $collapsed }) => ($collapsed ? "rotate(180deg)" : "none")};
    transition: transform 0.2s ease;
  }

  &:hover {
    opacity: 1;
  }
`;
